import type { CalculatorConfig } from '../../calculator';
import type { Section } from '../../types';

import { logger } from '../logger';
import { ApiError, estClient } from './estClient';

interface ApiLoanConfig {
  id: string;
  name: string;
  name_internal: string;
  description: string | null;
  loan_config_group_id: string | null;
  is_draft: '1' | '0';
  is_paused: '1' | '0';
  is_archived: '1' | '0';
  is_deleted: '1' | '0';
  is_active: '1' | '0';

  config_json: string;

  /**
   * @example '2024-05-20 19:29:07'
   */
  updated_at: string;
  updated_by: string;
  enabled_at: string | null;
  enabled_by: string | null;
  disabled_at: string | null;
  disabled_by: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
}

type ApiLoanConfigReach = Omit<ApiLoanConfig, 'config_json'> & {
  config_json: CalculatorConfig;
};

export interface LoanConfig extends ApiLoanConfigReach {
  status: 'active' | 'draft' | 'paused' | 'archived' | 'deleted';
}

export type NewLoanConfig = Pick<
  LoanConfig,
  'name' | 'name_internal' | 'description'
>;

export interface LoanUnitSegmentRef {
  id: string;

  /**
   * Reference to UnitSegment id
   */
  unit_segment_id: string;

  /**
   * Reference to corresponding LoanConfig.
   * Will be the same when binding requested by loan config id
   */
  loan_config_id: string;

  active_from: string | null;
  active_to: string | null;
}

interface SuccessResponse {
  success: true;
  message: string;
}

export interface LoanUnitSegmentsResponse {
  success: true;
  unit_segment_to_config: LoanUnitSegmentRef[];
}

export interface LoanConfigListResponse extends SuccessResponse {
  loan_config: ApiLoanConfig[];
}

export interface LoanConfigSearchResponse extends SuccessResponse {
  loan_config: ApiLoanConfigReach[];
  section: Section;
}

class LoanConfigApi {
  async getAll(): Promise<LoanConfig[]> {
    const resp = await estClient.get<LoanConfigListResponse>(
      '/api/db/loan_config',
    );

    return resp.loan_config.map(fromApi);
  }

  async getConfig(configId: string): Promise<LoanConfig | null> {
    const resp = await estClient.get<LoanConfigListResponse>(
      `/api/db/loan_config/${configId}`,
    );

    return resp.loan_config[0] ? fromApi(resp.loan_config[0]) : null;
  }

  async searchConfig({ unitId }: { unitId: string }): Promise<LoanConfig[]> {
    try {
      const resp = await estClient.get<LoanConfigSearchResponse>(
        '/api/ext/v1/loan/config/search',
        {
          query: {
            unit_id: unitId,
          },
        },
      );

      return resp.loan_config.map(fromApi);
    } catch (error) {
      if (error instanceof ApiError && String(error.code) === '1') {
        // "Unit ID `X` not found in DB"
        return [];
      }

      throw error;
    }
  }

  async create(newConfig: NewLoanConfig) {
    await estClient.post<LoanConfigListResponse>('/api/db/loan_config', {
      body: newConfig,
    });
  }

  async update(config: LoanConfig): Promise<LoanConfig> {
    const resp = await estClient.put<LoanConfigListResponse>(
      `/api/db/loan_config/${config.id}`,
      {
        body: toApi(config),
      },
    );

    return fromApi(resp.loan_config[0]);
  }

  async getUnitSegmentRefs(
    loanConfigId: string,
  ): Promise<LoanUnitSegmentRef[]> {
    const resp = await estClient.get<LoanUnitSegmentsResponse>(
      `/api/db/unit_segment_to_config/${loanConfigId}`,
    );

    return resp.unit_segment_to_config;
  }

  async addUnitSegmentRef({
    loanConfigId,
    unitSegmentId,
  }: {
    loanConfigId: string;
    unitSegmentId: string;
  }): Promise<LoanUnitSegmentRef[]> {
    const resp = await estClient.post<LoanUnitSegmentsResponse>(
      '/api/db/unit_segment_to_config',
      {
        body: {
          loan_config_id: loanConfigId,
          unit_segment_id: unitSegmentId,
        },
      },
    );

    return resp.unit_segment_to_config;
  }

  async removeUnitSegmentRef(
    unitSegmentRefId: string,
  ): Promise<LoanUnitSegmentRef[]> {
    const resp = await estClient.delete<LoanUnitSegmentsResponse>(
      `/api/db/unit_segment_to_config/${unitSegmentRefId}`,
    );

    return resp.unit_segment_to_config;
  }

  get CONFIG_GROUP() {
    return {
      INSTALLMENT: '1',
      MORTGAGE: '2',
    } as const;
  }

  get STATUS() {
    return {
      ACTIVE: 'active',
      DRAFT: 'draft',
      ARCHIVED: 'archived',
      PAUSED: 'paused',
      // this type isn't expected to be available on frontend
      DELETED: 'deleted',
    } as const;
  }

  get DEFAULT_CALCULATOR_CONFIG(): CalculatorConfig {
    return {
      name: '',
      description: '',
      rate: 0.2,
      loanStrategy: {
        type: 'simple',
        base: 'unpaidBody',
        method: 'annualPercent',
      },
      precision: 2,
      paymentFrequency: 1,
      term: {
        type: 'range',
        min: 1,
        max: 24,
        step: 1,
        limitByFinishDate: false,
        finishDateOffset: 0,
      },
      firstPayment: {
        type: 'range',
        min: 10,
        max: 70,
        step: 1,
      },
      variations: [],
      calculationCurrency: 'UAH',
      paymentCurrency: 'UAH',
    };
  }
}

function fromApi(rawConfig: ApiLoanConfig | ApiLoanConfigReach): LoanConfig {
  let status: LoanConfig['status'] = loanConfigApi.STATUS.DRAFT;

  if (rawConfig.is_active === '1') {
    status = loanConfigApi.STATUS.ACTIVE;
  } else if (rawConfig.is_archived === '1') {
    status = loanConfigApi.STATUS.ARCHIVED;
  } else if (rawConfig.is_paused === '1') {
    status = loanConfigApi.STATUS.PAUSED;
  }

  let config_json: LoanConfig['config_json'];

  if (rawConfig.config_json === '{}') {
    // it seems to be a new record that was not populated with any config yet
    // lets initialize it with default config
    // TODO: LoanSettings component seems to be a better place to initialize
    // however we already have fallbacks in case of errors here
    // therefore initialization was placed here too

    config_json = loanConfigApi.DEFAULT_CALCULATOR_CONFIG;
  } else if (typeof rawConfig.config_json === 'string') {
    try {
      config_json = JSON.parse(rawConfig.config_json);

      // if this field is not present we are dealing with invalid (test) data and
      // therefore ignore and reset to default props
      if (!config_json.loanStrategy) {
        throw new Error('Got invalid config structure');
      }
    } catch (error) {
      logger.error('Error parsing loan config', {
        config: rawConfig,
      });

      // TODO: this can be removed in future when we will be sure that backend
      // can not return malformed data to us
      config_json = loanConfigApi.DEFAULT_CALCULATOR_CONFIG;
    }
  } else {
    ({ config_json } = rawConfig);
  }

  return {
    ...rawConfig,
    config_json: {
      ...config_json,
      // TODO: remove this and store only on top level?
      name: rawConfig.name || '',
      description: rawConfig.description || '',
    },
    status,
  };
}

function toApi({ status, ...config }: LoanConfig): ApiLoanConfig {
  const flags: Pick<
    ApiLoanConfig,
    'is_active' | 'is_archived' | 'is_deleted' | 'is_draft' | 'is_paused'
  > = {
    is_active: '0',
    is_archived: '0',
    is_draft: '0',
    is_paused: '0',
    is_deleted: '0',
  };

  switch (status) {
    case loanConfigApi.STATUS.ACTIVE:
      flags.is_active = '1';
      break;
    case loanConfigApi.STATUS.ARCHIVED:
      flags.is_archived = '1';
      break;
    case loanConfigApi.STATUS.PAUSED:
      flags.is_paused = '1';
      break;
    default:
    case loanConfigApi.STATUS.DRAFT:
      flags.is_draft = '1';
      break;
  }

  return {
    ...config,
    ...flags,
    config_json: JSON.stringify({
      ...config.config_json,
      // TODO: decide whether we want to keep this props inside the config
      name: config.name,
      description: config.description,
    }),
  };
}

export const loanConfigApi = new LoanConfigApi();
